import React, { useEffect, useState } from "react";
import useFetch from "hooks/useFetch";
import SectionTabs from "pages/AdminLayout/SectionTabs";
import {
  Badge,
  Button,
  Card,
  EmptyState,
  Icon,
  IconButton,
  PageHeader,
  Pagination,
  Select,
  Skeleton,
  styles as ui,
  Textarea,
  TextField,
  useToast,
} from "stories/Admin";
import ContentModal from "./ContentModal";
import DuaAiPanel from "./DuaAiPanel";
import DuaAudioField from "./DuaAudioField";
import styles from "./DuaPage.module.scss";

interface Dua {
  id: number;
  audioId?: number | null;
  kategori: string;
  tittel: string;
  arabisk: string;
  arabisk1: string;
  oversettelse: string;
  engelsk?: string | null;
  urdu?: string | null;
  transliteration: string;
  referanse: string;
  tags: string;
}

const DuaPage: React.FC = () => {
  const { execute, isLoading } = useFetch();
  const toast = useToast();
  const [duas, setDuas] = useState<Dua[]>([]);
  const [filteredDuas, setFilteredDuas] = useState<Dua[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [selectedDua, setSelectedDua] = useState<Dua | null>(null);
  const [creating, setCreating] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [category, setCategory] = useState("");
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingAudio, setUploadingAudio] = useState(false);

  const fetchDuas = async () => {
    const duaResult = (await execute("/api/v1/duas", "GET")) as Dua[];
    if (duaResult) setDuas(duaResult);
    setLoaded(true);
  };

  useEffect(() => {
    fetchDuas();
  }, []);

  const uniqueCategories = Array.from(new Set(duas.map((dua) => dua.kategori)));

  useEffect(() => {
    const filtered = duas.filter((dua) => {
      const matchesSearch = dua.tittel
        .toLowerCase()
        .includes(searchTerm.toLowerCase());
      const matchesCategory = category === "" || dua.kategori === category;
      return matchesSearch && matchesCategory;
    });
    setFilteredDuas(filtered);
    setCurrentPage(1);
  }, [searchTerm, category, duas]);

  const handleEditClick = (dua: Dua) => {
    setCreating(false);
    setSelectedDua(dua);
  };

  const handleCreateClick = () => {
    setCreating(true);
    setSelectedDua({
      id: 0,
      kategori: "",
      tittel: "",
      arabisk: "",
      arabisk1: "",
      oversettelse: "",
      engelsk: "",
      urdu: "",
      transliteration: "",
      referanse: "",
      tags: "",
    });
  };

  const handleSaveChanges = async () => {
    if (selectedDua && !uploadingAudio) {
      if (
        creating &&
        [
          selectedDua.kategori,
          selectedDua.tittel,
          selectedDua.arabisk,
          selectedDua.oversettelse,
          selectedDua.referanse,
        ].some((value) => !value.trim())
      ) {
        toast.error(
          "Fyll inn kategori, tittel, arabisk, norsk oversettelse og referanse.",
        );
        return;
      }
      setSaving(true);
      try {
        const { id, ...fields } = selectedDua;
        const payload = creating
          ? {
              ...fields,
              arabisk1: selectedDua.arabisk
                .normalize("NFKC")
                .replace(
                  /[\u0610-\u061A\u064B-\u065F\u0670\u06D6-\u06ED\u0640]/g,
                  "",
                ),
              audioId: null,
            }
          : { ...selectedDua, audioId: undefined };
        const saved = await execute(
          creating ? "/api/v1/duas" : `/api/v1/duas/${id}`,
          creating ? "POST" : "PATCH",
          payload,
        );
        if (!saved) {
          toast.error("Duaen ble ikke lagret. Prøv igjen; feltene er beholdt.");
          return;
        }
        setSelectedDua(null);
        toast.success(creating ? "Duaen er lagt til." : "Duaen er oppdatert.");
        await fetchDuas();
      } finally {
        setSaving(false);
      }
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    if (selectedDua) {
      const { name, value } = e.target;
      setSelectedDua((prev) => (prev ? { ...prev, [name]: value } : prev));
    }
  };

  // Pagination calculations
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredDuas.slice(indexOfFirstItem, indexOfLastItem);
  const pageCount = Math.ceil(filteredDuas.length / itemsPerPage);

  const paginate = (pageNumber: number) => setCurrentPage(pageNumber);

  return (
    <>
      <PageHeader
        title="Duaer, navn og uttrykk"
        subtitle="Islamsk innhold i appen. Endringer vises med en gang."
        actions={<Button onClick={handleCreateClick}>Legg til dua</Button>}
      />
      <SectionTabs />

      <DuaAiPanel
        onDuasChanged={fetchDuas}
        categories={uniqueCategories.filter(Boolean)}
      />

      <div className={styles.filters}>
        <div className={styles.search}>
          <Icon
            name="search"
            size={18}
            strokeWidth={2}
            className={styles.searchIcon}
          />
          <TextField
            type="search"
            aria-label="Søk etter tittel"
            placeholder="Søk etter tittel …"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <Select
          aria-label="Kategori"
          fieldClassName={styles.category}
          value={category}
          onChange={(e) => setCategory(e.target.value)}
        >
          <option value="">Alle kategorier</option>
          {uniqueCategories.map((cat) => (
            <option key={cat} value={cat}>
              {cat}
            </option>
          ))}
        </Select>
      </div>

      {!loaded && isLoading ? (
        <div className={styles.grid}>
          {[0, 1, 2, 3].map((i) => (
            <Card key={i} className={styles.item}>
              <Skeleton width="50%" height={18} />
              <Skeleton height={36} />
              <Skeleton width="80%" />
            </Card>
          ))}
        </div>
      ) : currentItems.length === 0 ? (
        <EmptyState icon="book" title="Ingen duaer">
          {duas.length > 0
            ? "Ingen duaer passer søket eller kategorien."
            : "Fant ingen duaer."}
        </EmptyState>
      ) : (
        <div className={styles.grid}>
          {currentItems.map((dua) => (
            <Card key={dua.id} className={styles.item}>
              <div className={styles.itemHead}>
                <b className={styles.itemTitle}>{dua.tittel}</b>
                {dua.kategori && <Badge tone="mint">{dua.kategori}</Badge>}
                <IconButton
                  label="Rediger"
                  icon={<Icon name="edit" size={17} strokeWidth={1.9} />}
                  onClick={() => handleEditClick(dua)}
                />
              </div>
              {dua.arabisk && (
                <p className={`${ui.arabic} ${styles.arabic}`} lang="ar">
                  {dua.arabisk}
                </p>
              )}
              <span className={styles.translation}>{dua.oversettelse}</span>
            </Card>
          ))}
        </div>
      )}

      <Pagination
        page={currentPage}
        pageCount={pageCount}
        onChange={paginate}
      />

      <ContentModal
        open={!!selectedDua}
        title={creating ? "Legg til dua" : "Rediger dua"}
        saveLabel={creating ? "Legg til dua" : "Lagre endringer"}
        icon="book"
        saving={saving || uploadingAudio}
        onSave={handleSaveChanges}
        onClose={() => setSelectedDua(null)}
      >
        {selectedDua && (
          <>
            {creating && (
              <Select
                label="Kategori"
                value={selectedDua.kategori}
                onChange={(event) => {
                  const value = event.target.value;
                  setSelectedDua((prev) =>
                    prev ? { ...prev, kategori: value } : prev,
                  );
                }}
              >
                <option value="">Velg kategori</option>
                {uniqueCategories.filter(Boolean).map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
              </Select>
            )}
            <TextField
              label="Tittel"
              name="tittel"
              value={selectedDua.tittel}
              onChange={handleInputChange}
            />
            <Textarea
              label="Arabisk"
              name="arabisk"
              lang="ar"
              dir="rtl"
              rows={3}
              className={styles.arabicInput}
              value={selectedDua.arabisk}
              onChange={handleInputChange}
            />
            <Textarea
              label="Norsk oversettelse"
              name="oversettelse"
              rows={3}
              value={selectedDua.oversettelse}
              onChange={handleInputChange}
            />
            <Textarea
              label="Engelsk"
              name="engelsk"
              rows={3}
              value={selectedDua.engelsk ?? ""}
              onChange={handleInputChange}
            />
            <Textarea
              label="Urdu"
              name="urdu"
              lang="ur"
              dir="rtl"
              rows={3}
              value={selectedDua.urdu ?? ""}
              onChange={handleInputChange}
            />
            <Textarea
              label="Translitterasjon"
              name="transliteration"
              rows={3}
              value={selectedDua.transliteration}
              onChange={handleInputChange}
            />
            <div className={ui.g2}>
              <TextField
                label="Referanse"
                name="referanse"
                value={selectedDua.referanse}
                onChange={handleInputChange}
              />
              <TextField
                label="Tagger"
                name="tags"
                value={selectedDua.tags}
                onChange={handleInputChange}
              />
            </div>
            {!creating && (
              <DuaAudioField
                key={selectedDua.id}
                duaId={selectedDua.id}
                onBusyChange={setUploadingAudio}
              />
            )}
            {creating && (
              <p className={styles.audioHint}>
                Du kan laste opp lyd etter at duaen er opprettet.
              </p>
            )}
          </>
        )}
      </ContentModal>
    </>
  );
};

export default DuaPage;
